import React, { useCallback, useEffect } from 'react';
import { withLoading, SearchFieldWithDebounce } from '@adjust/dashboard-components';

import { AdvancedUser, User, UserRole } from '@adjust/dashboard-core/api/types';
import { UsersSection } from '@react/Pages/UsersPage/UsersSection/UsersSection';
import { getUsersGroupedByRole } from '@react/Pages/UsersPage/UsersPage/UsersPage.utils';
import { UserTile } from '@react/Pages/UsersPage/UserTile/UserTile';
import { AdvancedUserTile } from '@react/Pages/UsersPage/UserTile/AdvancedUserTile';
import { UserGroup } from '@react/Pages/UsersPage/UsersPage/UsersPage.types';

import './UsersPage.scss';

const userGroups = [
  UserGroup.SuperAdmins,
  UserGroup.Admins,
  UserGroup.Advanced,
  UserGroup.Editors,
  UserGroup.Readers,
  UserGroup.Custom,
];

const roleByGroup = {
  [UserGroup.SuperAdmins]: UserRole.SuperAdmin,
  [UserGroup.Admins]: UserRole.Admin,
  [UserGroup.Editors]: UserRole.Editor,
  [UserGroup.Readers]: UserRole.Reader,
  [UserGroup.Custom]: UserRole.Custom,
};

interface UserSectionRendererProps {
  group: UserGroup;
  users: User[];
  goToUserPermissions: (userId: number) => void;
  goToUserDetails: (userId: number) => void;
}

const UserSectionRenderer: React.FC<UserSectionRendererProps> = ({
  group,
  users,
  goToUserPermissions,
  goToUserDetails,
}) => {
  if (users.length === 0) {
    return null;
  }

  return (
    <UsersSection sectionName={group} key={group}>
      {users.map((user: User) => (
        <UserTile key={user.id} user={user} onPermissions={goToUserPermissions} onDetails={goToUserDetails} />
      ))}
    </UsersSection>
  );
};

interface AdvancedUserSectionRendererProps {
  advancedUsers: AdvancedUser[];
}

const AdvancedUserSectionRenderer: React.FC<AdvancedUserSectionRendererProps> = ({ advancedUsers }) => {
  if (advancedUsers.length === 0) {
    return null;
  }

  return (
    <UsersSection sectionName={UserGroup.Advanced} key={UserGroup.Advanced}>
      {advancedUsers.map((user: AdvancedUser) => (
        <AdvancedUserTile key={user.id} user={user} />
      ))}
    </UsersSection>
  );
};

export interface UsersPageStateProps {
  users: User[];
  advancedUsers: AdvancedUser[];
  searchTerm: string;
}

export interface UsersPageDispatchProps {
  fetchUsers: () => void;
  setSearchTerm: (value: string) => void;
}

export interface UsersPageOwnProps {
  goTo: (to: string, params?: { [key: string]: any }) => void;
  setQuery: (key: string, value: string) => void;
}

type UsersPageProps = UsersPageStateProps & UsersPageDispatchProps & UsersPageOwnProps;

const UsersPageBase: React.FC<UsersPageProps> = ({
  users,
  advancedUsers,
  fetchUsers,
  searchTerm,
  setSearchTerm,
  goTo,
  setQuery,
}) => {
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const goToUserPermissions = useCallback(
    (userId: number) => {
      goTo('accountSettings.users.access', { id: userId });
    },
    [goTo],
  );

  const goToUserDetails = useCallback(
    (userId: number) => {
      goTo('accountSettings.users.details', { id: userId });
    },
    [goTo],
  );

  const goToRoleSelection = useCallback(() => {
    goTo('accountSettings.users.role-selection');
  }, [goTo]);

  const goToAboutPermissions = useCallback(() => {
    goTo('accountSettings.users.about-permissions');
  }, [goTo]);

  const onSearchChange = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setQuery('user', trimmedValue);
      setSearchTerm(trimmedValue);
    },
    [setSearchTerm, setQuery],
  );

  const onSearchReset = useCallback(() => {
    onSearchChange('');
  }, [onSearchChange]);

  const usersGroupedByRole = getUsersGroupedByRole(users);

  const hasUsers = users.length > 0 || advancedUsers.length > 0;

  return (
    <>
      <div className="sub-actions">
        {/* @TODO: use UISref for proper href attribute when parent React component is connected to UIRouter */}
        <a onClick={goToRoleSelection}>
          <i className="icon-plus-alt" /> {t('label.add-user')}
        </a>
        {/* @TODO: use UISref for proper href attribute when parent React component is connected to UIRouter */}
        <a onClick={goToAboutPermissions}>
          <i className="icon-info-round" /> {t('label.about-permissions')}
        </a>

        <div className="flex-auto flex-box-row search-box-users">
          <SearchFieldWithDebounce
            onChange={onSearchChange}
            onReset={onSearchReset}
            border={false}
            wait={300}
            light
            name="users"
            placeholder={t('label.search-user')}
            value={searchTerm}
          />
        </div>
      </div>

      <div className="grid-list-container scroll-container users-sections-container">
        <div className="users-list grid-list">
          {userGroups.map(group =>
            group === UserGroup.Advanced ? (
              <AdvancedUserSectionRenderer key={group} advancedUsers={advancedUsers} />
            ) : (
              <UserSectionRenderer
                key={group}
                group={group}
                users={usersGroupedByRole[roleByGroup[group]] || []}
                goToUserPermissions={goToUserPermissions}
                goToUserDetails={goToUserDetails}
              />
            ),
          )}
        </div>
      </div>

      {!hasUsers && searchTerm && (
        <div className="no-data">
          <h2>{t('message.nothing-found')}</h2>
          <p>{t('message.no-result-found')}</p>
        </div>
      )}

      {!hasUsers && !searchTerm && (
        <div className="no-data">
          <h2>{t('users.no-users.message')}</h2>
          {/* @TODO: use UISref for proper href attribute when parent React component is connected to UIRouter */}
          <a onClick={goToRoleSelection} className="btn btn-main">
            {t('label.add-user')}
          </a>
        </div>
      )}
    </>
  );
};

export const UsersPage = withLoading(UsersPageBase, true);
